import {
    Component,
    ViewChild,
    OnInit,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    ChangeDetectorRef,
    EventEmitter,
    OnDestroy,
    Output,
} from '@angular/core';
import { Subscription, BehaviorSubject } from 'rxjs';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { FilterDataSource } from 'app/shared/data/filter-data-source';
import { StatusCodeService } from '../../../shared/services/status-code.service';
import { GenerateApiKeyComponent } from '../generate-api-key/generate-api-key.component';
import { ApiKey } from 'app/shared/models/api-key';
import { ApiKeyService } from '../../../shared/services/api-key.service';
import { ActivatedRoute } from '@angular/router';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { PageEvent } from '@angular/material/paginator';
import { CustomerEditorPaginationState } from 'app/pages/customer-editor/customer-editor-footer.model';
import { IComponentModalResult } from 'app/shared/models/modal';
import { ApiKeyDialogHintState, DomOperationUtilsService } from 'app/shared/utils/dom-operation-utils.service';

@Component({
    selector: 'app-api-key-dashboard',
    templateUrl: './api-key-dashboard.component.html',
    styles: [],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiKeyDashboardComponent implements OnInit, OnDestroy {
    @Output() public paginationStateChange = new EventEmitter<CustomerEditorPaginationState>();

    public selectedCustomerID: number;
    public apiKeys = new Array<ApiKey>();

    public apiKeySummary = new Array<ApiKey>();
    private subscriptions = new Array<Subscription>();
    public apiKeyDataChange: BehaviorSubject<ApiKey[]> = new BehaviorSubject<ApiKey[]>([]);
    public initialData = new BehaviorSubject<ApiKey[]>([]);
    public apiKeyDataSource: FilterDataSource<ApiKey> | null;
    public totalPaginationLength: number;
    @ViewChild(MatPaginator) public userPaginator: MatPaginator;
    @ViewChild(MatSort) public userSort: MatSort;
    public apiKeyFilterColumns = ['description', 'key', 'lastUsed', 'active', 'deleteApiKey'];
    public deleteApiKeyTitle: string;
    public deleteApiKeyMessage: string;
    public deleteApiKeyCancelText: string;
    public deleteApiKeyOkText: string;
    private apiKeyDialogRef?: MatDialogRef<GenerateApiKeyComponent, IComponentModalResult>;

    /**
     * Indicates the loading state of the component.
     */
    public isLoading: boolean;

    constructor(
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private apiKeyService: ApiKeyService,
        private route: ActivatedRoute,
        private uiUtilsService: UiUtilsService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private statusCodeService: StatusCodeService,
        private domOperationUtilsService: DomOperationUtilsService,
    ) {}
    /**
     * Function to open a dialog for api key creation.
     */
    public createApiKey(): void {
        if (this.apiKeyDialogRef) {
            return;
        }

        this.apiKeyDialogRef = this.dialog.open<GenerateApiKeyComponent, Record<string, never>, IComponentModalResult>(
            GenerateApiKeyComponent,
            {
                disableClose: true,
                hasBackdrop: false,
                data: {},
            },
        );
        this.domOperationUtilsService.apiKeyDialogHintState.next('create');

        const dialogSubscription = this.apiKeyDialogRef.afterClosed().subscribe(
            (result: IComponentModalResult | undefined) => {
                this.apiKeyDialogRef = undefined;
                this.domOperationUtilsService.apiKeyDialogHintState.next('closed');
                if (this.domOperationUtilsService.showpageHint.getValue()) {
                    this.domOperationUtilsService.selectedHintPageName.next('edit-cus-api');
                    setTimeout(() => this.domOperationUtilsService.editCusApi());
                }
                if (result?.success) {
                    this.isLoading = true;
                    this.showChangesSavedToast();
                    this.getApiKeySummary();
                    this.uiUtilsService.safeChangeDetection(this.cdr);
                }
            },
        );
        this.subscriptions.push(dialogSubscription);
    }

    public ngOnInit() {
        const hintDialogSubscription = this.domOperationUtilsService.apiKeyDialogHintState.subscribe(
            (state: ApiKeyDialogHintState) => {
                if (state === 'create' && !this.apiKeyDialogRef) {
                    this.createApiKey();
                } else if (state === 'closed' && this.apiKeyDialogRef) {
                    this.apiKeyDialogRef.close({ success: false });
                }
            },
        );
        this.subscriptions.push(hintDialogSubscription);

        this.translate.get('ADMIN.API_KEY_DASHBOARD.DELETE_API_KEY_TITLE').subscribe((res) => {
            this.deleteApiKeyTitle = res;
        });
        this.translate.get('ADMIN.API_KEY_DASHBOARD.DELETE_API_KEY_MESSAGE').subscribe((res) => {
            this.deleteApiKeyMessage = res;
        });
        this.translate.get('ADMIN.API_KEY_DASHBOARD.CANCEL_TEXT').subscribe((res) => {
            this.deleteApiKeyCancelText = res;
        });
        this.translate.get('ADMIN.API_KEY_DASHBOARD.OK_TEXT').subscribe((res) => {
            this.deleteApiKeyOkText = res;
        });
        this.isLoading = true;
        this.totalPaginationLength = 0;
        this.statusCodeService.addNewCustomer.subscribe((addNewCustomerFlag: boolean) => {
            // get current query params for customer
            if (addNewCustomerFlag) {
                this.route.queryParams.subscribe((params) => {
                    this.selectedCustomerID = params['c'];
                });
            } else {
                this.route.queryParams.subscribe((params) => {
                    this.selectedCustomerID = params['cid'];
                });
            }
        });

        this.getApiKeySummary();
        this.statusCodeService.statusCodeFlag.subscribe((statusCodeFlagValue) => {
            if (statusCodeFlagValue === true) {
                this.isLoading = false;
            }
        });
    }

    /**
     * Function to get all api keys for customer
     */
    public getApiKeySummary() {
        this.apiKeys = [];
        // get all customer summary
        const apiKeysummarySubscription = this.apiKeyService.getApiKeySummary(this.selectedCustomerID).subscribe(
            (apiKeySummaryData: ApiKey[]) => {
                if (apiKeySummaryData) {
                    this.isLoading = false;
                    this.apiKeys = apiKeySummaryData;
                    this.generateUserTable();
                } else if (apiKeySummaryData == null) {
                    this.isLoading = false;
                    this.uiUtilsService.safeChangeDetection(this.cdr);
                    this.generateUserTable();
                }
            },
            () => {
                this.isLoading = false;
                this.totalPaginationLength = 0;
                this.emitPaginationState();
                this.uiUtilsService.safeChangeDetection(this.cdr);
            },
        );
        this.subscriptions.push(apiKeysummarySubscription);
    }

    /**
     * Method generates a table of api keys
     */
    public generateUserTable() {
        this.apiKeyDataSource = null;
        this.apiKeySummary = [];
        this.apiKeyDataChange = new BehaviorSubject<ApiKey[]>([]);
        this.apiKeys.forEach((apiKey) => {
            const apiKeyCopiedData = this.apiKeySummary;

            const data = {
                cid: apiKey.cid,
                key: apiKey.key,
                lastUsed: apiKey.lastUsed,
                active: apiKey.active,
                description: apiKey.description,
            };
            apiKeyCopiedData.push(data);
            this.apiKeyDataChange.next(apiKeyCopiedData);
        });
        this.apiKeyDataSource = new FilterDataSource(
            this.apiKeyDataChange,
            this.apiKeys,
            this.userPaginator,
            this.userSort,
            this.apiKeyFilterColumns,
        );
        this.userPaginator.pageIndex = 0;
        this.totalPaginationLength = this.apiKeys.length;
        this.isLoading = false;
        this.emitPaginationState();
        this.uiUtilsService.safeChangeDetection(this.cdr);
    }

    public applyPagination(page: PageEvent): void {
        this.userPaginator.pageIndex = page.pageIndex;
        this.userPaginator.pageSize = page.pageSize;
        this.userPaginator.page.emit(page);
        this.emitPaginationState();
    }

    private emitPaginationState(): void {
        this.paginationStateChange.emit({
            length: this.totalPaginationLength,
            pageIndex: this.userPaginator?.pageIndex ?? 0,
            pageSize: this.userPaginator?.pageSize ?? 10,
            pageSizeOptions: [10, 25, 100],
        });
    }

    public onChange(e: MatSlideToggleChange, keyDetail: ApiKey): void {
        this.isLoading = true;

        const editApiKeySubscription = this.apiKeyService
            .editApiKey(this.selectedCustomerID, keyDetail, e.checked)
            .subscribe(
                () => {
                    this.isLoading = false;
                    this.showChangesSavedToast();
                    this.uiUtilsService.safeChangeDetection(this.cdr);
                    this.getApiKeySummary();
                },
                () => {
                    this.isLoading = false;
                    this.uiUtilsService.safeChangeDetection(this.cdr);
                    this.getApiKeySummary();
                },
            );

        this.subscriptions.push(editApiKeySubscription);
    }

    public deleteApiKey(keyDetail: ApiKey) {
        this.dialog
            .open(ConfirmationDialogComponent, {
                data: {
                    title: this.deleteApiKeyTitle,
                    message: this.deleteApiKeyMessage,
                    cancelText: this.deleteApiKeyCancelText,
                    okText: this.deleteApiKeyOkText,
                },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res.whichButtonWasPressed === 'ok') {
                    this.isLoading = true;

                    const deleteApiKeySubscription = this.apiKeyService
                        .deleteApiKey(this.selectedCustomerID, keyDetail)
                        .subscribe(
                            () => {
                                this.isLoading = false;
                                this.showChangesSavedToast();

                                this.uiUtilsService.safeChangeDetection(this.cdr);
                                this.getApiKeySummary();
                            },
                            () => {
                                this.isLoading = false;
                                this.uiUtilsService.safeChangeDetection(this.cdr);
                                this.getApiKeySummary();
                            },
                        );

                    this.subscriptions.push(deleteApiKeySubscription);
                }
            });
    }

    private showChangesSavedToast(): void {
        this.snackBar.open(
            this.translate.instant('CUSTOMER_EDITOR.CHANGES_SAVED_TO_CUSTOMER'),
            this.translate.instant('COMMON.DISMISS_TEXT'),
            { duration: 10000 },
        );
    }

    /**
     * Destroy the subscription data
     */
    public ngOnDestroy() {
        this.apiKeyDialogRef?.close({ success: false });
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.domOperationUtilsService.apiKeyDialogHintState.next('closed');
    }
}
